import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button, Card, Input } from "../components/ui";
import Footer from "../components/Footer";
import { ArrowLeft, CheckCircle } from "lucide-react";

/** Player can't log in: send a reset request to their club admin, who shares a link on WhatsApp. */
export default function ForgotPasswordPage() {
  const loc = useLocation();
  const [email, setEmail] = useState(loc.state?.email || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset-request", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell flex flex-col">
      <div className="flex-1">
        <div className="flex justify-center pt-12 pb-8">
          <img src="/logo.png" alt="Cricso" className="h-32 w-32 object-contain" />
        </div>

        {sent ? (
          <div className="px-6 text-center" data-testid="reset-request-sent">
            <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center mx-auto mb-4">
              <CheckCircle size={32} className="text-brand" />
            </div>
            <h1 className="text-2xl font-bold">Request sent to your admin</h1>
            <p className="text-ink-muted mt-2 text-[15px]">
              Your club admin has been asked to reset the password for <b className="text-ink">{email.trim()}</b>.
              They'll share a password reset link with you on WhatsApp.
            </p>
            <p className="text-ink-muted mt-3 text-sm">
              Open that link, set a new password, then sign in. Haven't heard back? Give your admin a nudge.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-brand font-semibold text-sm mt-8" data-testid="forgot-back-login">
              <ArrowLeft size={16} /> Back to Sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="px-6 pb-6">
              <h1 className="text-3xl font-bold leading-tight text-center">
                Forgot your <span className="text-brand">password?</span>
              </h1>
              <p className="text-ink-muted mt-2 text-[15px] text-center">
                Enter your email and we'll ask your club admin to send you a reset link on WhatsApp.
              </p>
            </div>
            <Card className="mx-5 mb-5">
              <form onSubmit={submit} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  placeholder="you@club.in"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
                <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? "Sending…" : "Request password reset"}
                </Button>
              </form>
            </Card>
            <div className="text-center text-sm mt-2 pb-10">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-brand font-semibold">
                <ArrowLeft size={16} /> Back to Sign in
              </Link>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
